import React, { useContext } from 'react'
import { SurfacePalette } from '../../../tokens'
import { useSprint } from 'hooks/api/useSprint'
import { sectionNumberFor } from '../../sections'
import { PresentPage } from '../PresentPage'
import { CreativeCardList } from '../../sections/CreativeCardList'
import { useCreativeApproval } from '../../sections/useCreativeApproval'
import { useCreativePreview } from '../../sections/useCreativePreview'
import { PresentNavLockContext } from '../PresentNavLock'

interface Props {
  palette: SurfacePalette
  businessID: number
  packetID: number
  sprintID?: number
}

/**
 * Present-mode (dark) Creative slide. The slide chrome carries the packet-local
 * `section` anchor; each creative card inside carries its own `creative` anchor.
 * In-meeting approval is enabled here too (the client is present), reusing the
 * shared approval flow; while a modal is open we lock deck navigation so Arrow/Esc
 * don't move slides or exit the presentation mid-edit.
 */
export const PresentCreativeSection = ({ palette, businessID, packetID, sprintID }: Props) => {
  const { sprintCreatives, sprintCreativesLoading } = useSprint(sprintID)
  const setNavLocked = useContext(PresentNavLockContext)
  const { onApprove, onRemoveApproval, modals } = useCreativeApproval(
    businessID,
    palette,
    setNavLocked
  )
  // In-meeting approval is enabled here too, surfaced from the preview action bar
  // (the client sees the full ad before the AE approves). No editor link in
  // Present — it stays read-only for source edits.
  const {
    onPreview,
    prefetch,
    modals: previewModals,
  } = useCreativePreview(palette, setNavLocked, {
    onApprove,
    onRemoveApproval,
  })

  return (
    <PresentPage
      palette={palette}
      number={sectionNumberFor('creative')}
      title="Creative"
      commentAnchor={{ type: 'section', id: `${packetID}:creative`, label: 'Creative' }}
    >
      <CreativeCardList
        palette={palette}
        creatives={sprintCreatives}
        loading={sprintCreativesLoading}
        noSprint={!sprintID}
        onPreview={onPreview}
        onPrefetch={prefetch}
      />
      {modals}
      {previewModals}
    </PresentPage>
  )
}
